import { t } from "@hakili/i18n";
import { Button, Card, Field } from "@hakili/ui";
import { useState } from "react";

/**
 * `P0-DLW-001`: the dealership administrator's login.
 *
 * **The partner path, not the client OTP path** , `00-SPINE` R-07. A
 * dealership's people are staff of a partner organisation: email, password, and
 * the invitation that set it. The client door with its handset and its code is
 * a different actor's door.
 *
 * **The server's own sentence is shown**, as the console's login does. A locked
 * account, a wrong password and an unreachable server are three different facts,
 * and one generic line turns all of them into a dead end.
 */

export interface ConnexionProps {
  readonly connecter: (identifiants: { email: string; password: string }) => Promise<void>;
  /** `P0-DLW-004`: why this screen is showing, when the api authenticated somebody this surface will not admit. */
  readonly motif?: string;
}

export function Connexion({ connecter, motif }: ConnexionProps): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (): Promise<void> => {
    if (enCours) return;
    setErreur(null);
    setEnCours(true);
    try {
      await connecter({ email, password: motDePasse });
    } catch (cause) {
      // The server's sentence when there is one; `ApiError` carries it on
      // `message`. Anything else is a transport failure and says so.
      const message = cause instanceof Error && cause.message !== "" ? cause.message : "";
      setErreur(message === "" ? t("common.error.inattendue") : message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="mx-auto mt-16 w-full max-w-sm">
      <Card>
        <h1 className="text-lg font-medium text-gray-900">{t("dealer.connexion.titre")}</h1>

        <Field label={t("dealer.connexion.email")} htmlFor="dealer-email">
          <input
            id="dealer-email"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            data-testid="connexion-email"
          />
        </Field>

        <Field label={t("dealer.connexion.motDePasse")} htmlFor="dealer-motdepasse">
          <input
            id="dealer-motdepasse"
            type="password"
            value={motDePasse}
            onChange={(event) => {
              setMotDePasse(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            data-testid="connexion-motdepasse"
          />
        </Field>

        <div className="mt-4">
          <Button
            onClick={() => {
              void soumettre();
            }}
            data-testid="connexion-entrer"
          >
            {t("dealer.connexion.entrer")}
          </Button>
        </div>

        {erreur === null ? null : (
          <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="connexion-erreur">
            {erreur}
          </p>
        )}

        {/* `P0-DLW-004`: shown when the api authenticated somebody this surface
            will not admit. A refusal without a sentence sends a person back to
            the password they just used. */}
        {motif === undefined ? null : (
          <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="connexion-refus">
            {motif}
          </p>
        )}
      </Card>
    </div>
  );
}
